/**
 * Axios Instance with Authentication Interceptors
 *
 * Security: Uses HttpOnly cookies for authentication (set by backend).
 * Cookies are automatically sent with `withCredentials: true`.
 *
 * Features:
 * - Automatic cookie-based authentication
 * - Response interception for 401 errors
 * - Automatic logout and redirect on session expiration
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore, dispatchAuthEvent } from '../store/authStore';
import i18n from '../i18n';

// API Base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

// E2E Test Key - read from localStorage (set by Playwright) or environment
const getE2ETestKey = (): string | null => {
  // Try localStorage first (set by Playwright addInitScript)
  if (typeof window !== 'undefined') {
    const key = localStorage.getItem('e2e_test_key');
    if (key) return key;
  }
  // Fall back to environment variable
  return import.meta.env.VITE_E2E_TEST_KEY ?? null;
};

// Session expiration message - uses i18n for translation
const getSessionExpiredMessage = () => i18n.t('auth.sessionExpiredMessage');

// Error codes from backend
const AUTH_ERROR_CODES = ['TOKEN_EXPIRED', 'INVALID_TOKEN', 'UNAUTHORIZED'] as const;

interface ApiErrorResponse {
  error?: {
    code?: string;
    message?: string;
  };
  message?: string;
}

/**
 * Handle session expiration - logout and redirect
 */
const handleSessionExpired = () => {
  const authStore = useAuthStore.getState();

  // Only logout if currently authenticated (prevent multiple triggers)
  if (authStore.isAuthenticated) {
    // Dispatch event for toast notification
    dispatchAuthEvent('SESSION_EXPIRED', { message: getSessionExpiredMessage() });

    // Perform logout (this will clear state and call backend to clear cookie)
    authStore.logout(false); // Don't show toast again, we dispatched event
  }
};

/**
 * Create and configure Axios instance
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    // IMPORTANT: Enable credentials for HttpOnly cookie authentication
    // This allows the browser to send/receive cookies with cross-origin requests
    withCredentials: true,
  });

  /**
   * REQUEST INTERCEPTOR
   * - Adds E2E test key header (for bypassing rate limits in tests)
   * - Cookies are sent automatically with withCredentials: true
   */
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Add E2E test key header if available (bypasses rate limiting)
      // Header: X-E2E-Test-K3y-f1n
      const e2eTestKey = getE2ETestKey();
      if (e2eTestKey) {
        config.headers['X-E2E-Test-K3y-f1n'] = e2eTestKey;
      }

      // Note: Authentication is handled via HttpOnly cookies
      // The browser automatically sends cookies with withCredentials: true
      // No need to manually add Authorization header

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  /**
   * RESPONSE INTERCEPTOR
   * - Catches 401 Unauthorized responses
   * - Triggers logout on token expiration errors
   */
  instance.interceptors.response.use(
    // Success handler - pass through
    (response) => response,

    // Error handler
    (error: AxiosError<ApiErrorResponse>) => {
      // Check if this is a cancelled request (from our proactive check)
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      // Check for 401 Unauthorized
      if (error.response?.status === 401) {
        const errorData = error.response.data;
        const errorCode = errorData?.error?.code;

        // Check if it's an auth-related error
        if (
          AUTH_ERROR_CODES.includes(errorCode as typeof AUTH_ERROR_CODES[number]) ||
          error.response.status === 401
        ) {
          handleSessionExpired();
        }
      }

      // Reject the promise so calling code knows the request failed
      return Promise.reject(error);
    }
  );

  return instance;
};

// Export singleton instance
export const axiosInstance = createAxiosInstance();

// Export helper for making authenticated requests
export const api = {
  get: <T>(url: string, config?: Parameters<typeof axiosInstance.get>[1]) =>
    axiosInstance.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: Parameters<typeof axiosInstance.post>[2]) =>
    axiosInstance.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: Parameters<typeof axiosInstance.put>[2]) =>
    axiosInstance.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: Parameters<typeof axiosInstance.patch>[2]) =>
    axiosInstance.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: Parameters<typeof axiosInstance.delete>[1]) =>
    axiosInstance.delete<T>(url, config).then((res) => res.data),
};

export default axiosInstance;
